import { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { listMeetingsOpenForGuests } from '../lib/guestInvites';

const EMPTY_FORM = {
  name: '',
  businessName: '',
  email: '',
  phone: '',
  industry: '',
  servicesProvided: '',
  payer: '',
  meetingId: '',
};

function meetingLabel(meeting) {
  const when = new Date(meeting.startsAt).toLocaleString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return meeting.isToday ? `${when} (today)` : when;
}

export function GuestFeeNotice() {
  return (
    <p className="mt-2 text-sm text-gray-700" data-testid="guest-fee-notice">
      The guest fee is <strong>$30</strong> for the meeting. It applies whether or not the guest eats.
    </p>
  );
}

export default function InviteGuestModal({ isOpen, onClose, onSubmit }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [meetings, setMeetings] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    listMeetingsOpenForGuests()
      .then((resp) => {
        if (cancelled) return;
        const list = resp?.meetings ?? [];
        setMeetings(list);
        // Default to the soonest, which is what the guest usually means.
        setFormData((prev) => ({ ...prev, meetingId: prev.meetingId || (list[0]?.id ?? '') }));
      })
      .catch(() => {
        if (!cancelled) setMeetings([]);
      });
    return () => { cancelled = true; };
  }, [isOpen]);

  const canSubmit = useMemo(() => {
    return Boolean(
      String(formData.name).trim()
      && String(formData.businessName).trim()
      && String(formData.email).trim()
      // Who pays is a decision the member has to make up front; the fee cannot
      // be requested without it.
      && String(formData.payer).trim()
    );
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    await onSubmit({
      name: String(formData.name).trim(),
      businessName: String(formData.businessName).trim(),
      email: String(formData.email).trim(),
      phone: String(formData.phone).trim() || null,
      industry: String(formData.industry).trim() || null,
      servicesProvided: String(formData.servicesProvided).trim() || null,
      payer: String(formData.payer).trim(),
      meetingId: String(formData.meetingId).trim() || null,
    });

    setFormData(EMPTY_FORM);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                <div className="px-6 py-5">
                  <Dialog.Title className="text-xl font-playfair-sans text-NBNA-primary">
                    Invite a Guest
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-600">
                    This creates or updates a limited guest profile tied to the guest&apos;s email.
                  </p>

                  <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="invite-guest-meeting">Meeting</label>
                      <select
                        id="invite-guest-meeting"
                        className="input-field w-full"
                        data-testid="invite-guest-meeting"
                        value={formData.meetingId}
                        onChange={(e) => setFormData((prev) => ({ ...prev, meetingId: e.target.value }))}
                        required
                      >
                        {meetings.length === 0 ? (
                          <option value="">No meetings are open for guests</option>
                        ) : null}
                        {meetings.map((m) => (
                          <option key={m.id} value={m.id}>{meetingLabel(m)}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="invite-guest-name">Name</label>
                      <input
                        id="invite-guest-name"
                        type="text"
                        className="input-field w-full"
                        value={formData.name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="invite-guest-business">Business name</label>
                      <input
                        id="invite-guest-business"
                        type="text"
                        className="input-field w-full"
                        value={formData.businessName}
                        onChange={(e) => setFormData((prev) => ({ ...prev, businessName: e.target.value }))}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="invite-guest-email">Email</label>
                        <input
                          id="invite-guest-email"
                          type="email"
                          className="input-field w-full"
                          value={formData.email}
                          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="invite-guest-phone">Phone (optional)</label>
                        <input
                          id="invite-guest-phone"
                          type="tel"
                          className="input-field w-full"
                          value={formData.phone}
                          onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="invite-guest-industry">Industry (optional)</label>
                        <input
                          id="invite-guest-industry"
                          type="text"
                          className="input-field w-full"
                          value={formData.industry}
                          onChange={(e) => setFormData((prev) => ({ ...prev, industry: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="invite-guest-services">Short description (optional)</label>
                        <input
                          id="invite-guest-services"
                          type="text"
                          className="input-field w-full"
                          value={formData.servicesProvided}
                          onChange={(e) => setFormData((prev) => ({ ...prev, servicesProvided: e.target.value }))}
                        />
                      </div>
                    </div>

                    <fieldset className="rounded-md border border-gray-200 p-4">
                      <legend className="px-1 text-sm font-medium text-gray-700">
                        Who pays the guest fee?
                      </legend>

                      <div className="mt-1 space-y-2">
                        <label className="flex items-start gap-2" htmlFor="invite-guest-payer-member">
                          <input
                            id="invite-guest-payer-member"
                            type="radio"
                            name="invite-guest-payer"
                            className="mt-1"
                            value="member"
                            checked={formData.payer === 'member'}
                            onChange={() => setFormData((prev) => ({ ...prev, payer: 'member' }))}
                            required
                          />
                          <span className="text-sm text-gray-800">
                            I&apos;ll pay — the receipt comes to me
                          </span>
                        </label>

                        <label className="flex items-start gap-2" htmlFor="invite-guest-payer-guest">
                          <input
                            id="invite-guest-payer-guest"
                            type="radio"
                            name="invite-guest-payer"
                            className="mt-1"
                            value="guest"
                            checked={formData.payer === 'guest'}
                            onChange={() => setFormData((prev) => ({ ...prev, payer: 'guest' }))}
                          />
                          <span className="text-sm text-gray-800">
                            My guest pays — the receipt goes to them
                          </span>
                        </label>
                      </div>

                      <GuestFeeNotice />

                      <p className="mt-2 text-sm text-gray-600">
                        Payment is requested after the Leadership Team confirms there&apos;s no conflict
                        with an existing member.
                      </p>
                    </fieldset>

                    <div className="mt-6 flex justify-end gap-3">
                      <button type="button" className="btn-secondary" onClick={onClose}>
                        Cancel
                      </button>
                      <button type="submit" className="btn-primary" disabled={!canSubmit}>
                        Invite Guest
                      </button>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
